import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-984d7358"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["onClick"]
const _hoisted_2 = ["id"]
const _hoisted_3 = ["onClick"]
const _hoisted_4 = {
  key: 0,
  class: "absolute bottom-0 w-full h-[44px] bg-white pl-6 font-oswald text-[12.8px] text-zbr-gray-700 flex items-center border-y-2 border-zbr-gray-300"
}
const _hoisted_5 = ["onClick"]
const _hoisted_6 = {
  key: 0,
  class: "pt-3 font-oswald text-sm text-center"
}

import { ref, computed, watch, onMounted, useSlots } from 'vue';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import IconInformationTransparent from '@/components/icons/IconInformationTransparent.vue';
import { DynamicKeyValue } from '@/utils/types/index';
import { useDebounceFn } from '@vueuse/core';
import { useScreenSize } from '@/composables/useScreenSize';
import { useVehicleResultHelper } from '@/composables/useVehicleResultHelper';
import {
  TyrePairItem,
  TyreBikeModelTyreItem,
  TyreItemResponse,
  TyreBargainItem,
  TyreDiscountItem,
  TyreAccessoryItem,
} from '@/utils/types/tyres';
import { RowsError, CombinationType } from '@/utils/types/baseTableType';

// ==============================================
// Types
// ==============================================
interface BaseTableField {
  text: string;
  value: string;
  align?: string;
  flexJustify?: string;
  sortable?: boolean;
  isAction?: boolean;
  minWidth: {
    tablet?: string;
    desktop?: string;
    desktopLg?: string;
  };
  maxWidth: {
    tablet?: string;
    desktop?: string;
    desktopLg?: string;
  };
  colSpan?: string;
  isHiddenTextMobile?: boolean;
  noPaddingLeft?: boolean;
}

interface RowClickEvent {
  tableItem: DynamicKeyValue;
  index: number;
}

interface ToggleTableRowExpandEvent {
  tableItem: DynamicKeyValue;
  index: number;
  expandRowStatus: boolean | undefined;
}

interface ExpandStatus {
  [key: string]: boolean | undefined;
}

interface ShowRowMoreInfo {
  [key: string]: boolean | undefined;
}

interface DynamicScrollerTableProps {
  items: any[];
  isLoading: boolean;
  isPageEnd: boolean;
  maxHeight?: string;
  keyField?: string;
  tableFields: BaseTableField[];
  tableFieldsTablet?: BaseTableField[];
  tableFieldsTabletMoreInfo?: BaseTableField[];
  tableBodyClass?: string;
  isCombination?: boolean;
  clickable?: boolean;
  expandColSpan?: string;
  hasPaddingLeft?: boolean;
  customPaddingLeft?: string;
  customPaddingRight?: string;
  customGap?: string;
  rowsTableError?: RowsError;
  isVehicleSearch?: boolean;
  showAllField?: boolean;
  alwaysShowMoreInfo?: boolean;
  isInitOpenShowMoreInfo?: boolean;
  inverse?: boolean;
  combinationType?: CombinationType;
  hasInfoFieldDesktop?: boolean;
}

// ==============================================
// Props
// ==============================================

export default /*#__PURE__*/_defineComponent({
  __name: 'DynamicScrollerTable',
  props: {
    items: { default: () => [] },
    isLoading: { type: Boolean, default: false },
    isPageEnd: { type: Boolean, default: false },
    maxHeight: { default: '' },
    keyField: { default: 'articleNo' },
    tableFields: { default: () => [] },
    tableFieldsTablet: { default: () => [] },
    tableFieldsTabletMoreInfo: { default: () => [] },
    tableBodyClass: { default: '' },
    isCombination: { type: Boolean, default: false },
    clickable: { type: Boolean, default: true },
    expandColSpan: { default: '' },
    hasPaddingLeft: { type: Boolean, default: true },
    customPaddingLeft: { default: '' },
    customPaddingRight: { default: '' },
    customGap: { default: '' },
    rowsTableError: { default: undefined },
    isVehicleSearch: { type: Boolean, default: false },
    showAllField: { type: Boolean, default: false },
    alwaysShowMoreInfo: { type: Boolean, default: false },
    isInitOpenShowMoreInfo: { type: Boolean, default: false },
    inverse: { type: Boolean, default: false },
    combinationType: { default: undefined },
    hasInfoFieldDesktop: { type: Boolean, default: false }
  },
  emits: ["next-page", "row-click", "toggle-table-row-expand", "close-table-row-expand", "toggle-row-more-info"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;



// ==============================================
// Emits
// ==============================================


// ==============================================
// Composables
// ==============================================
const { isTablet } = useScreenSize();
const { isNoInfoAndArticleAA, isNoInfoAndArticle88, isArticle88, isArticle99 } =
  useVehicleResultHelper();
const slots = useSlots();

// ==============================================
// States
// ==============================================
const scrollerRef = ref<InstanceType<typeof DynamicScroller> | null>(null);
const pageNumber = ref(1);
const isDisabledNextPage = ref(false);

// State management keyed by item id for virtual scrolling compatibility
const expandRowStatus = ref<ExpandStatus>({});
const expandRowStatusFront = ref<ExpandStatus>({});
const expandRowStatusRear = ref<ExpandStatus>({});
const showRowMoreInfo = ref<ShowRowMoreInfo>({});
const showRowMoreInfoFront = ref<ShowRowMoreInfo>({});
const showRowMoreInfoRear = ref<ShowRowMoreInfo>({});

// ==============================================
// Computed
// ==============================================
const computedTableFields = computed(() => {
  if (
    isTablet.value &&
    props.tableFieldsTablet &&
    props.tableFieldsTablet.length > 0
  ) {
    return props.tableFieldsTablet;
  }
  return props.tableFields;
});

// Tailwind spacing scale (number to pixels)
const tailwindSpacingToPx: Record<string, number> = {
  '0': 0,
  '0.5': 2,
  '1': 4,
  '1.5': 6,
  '2': 8,
  '2.5': 10,
  '3': 12,
  '3.5': 14,
  '4': 16,
  '5': 20,
  '6': 24,
  '7': 28,
  '8': 32,
  '9': 36,
  '10': 40,
  '11': 44,
  '12': 48,
  '14': 56,
  '16': 64,
  '20': 80,
  '24': 96,
  '28': 112,
  '32': 128,
  '36': 144,
  '40': 160,
  '44': 176,
  '48': 192,
  '52': 208,
  '56': 224,
  '60': 240,
  '64': 256,
  '72': 288,
  '80': 320,
  '96': 384,
};

// Helper function to parse Tailwind padding class to pixels
// Supports classes like 'pl-12', 'pr-6', 'pl-12 xl:pl-0'
function parseTailwindPaddingToPx(
  classes: string | undefined,
  direction: 'left' | 'right',
): number {
  if (!classes) return 0;

  const prefix = direction === 'left' ? 'pl-' : 'pr-';
  // Find base class (without responsive prefix) - mobile first approach
  const classArray = classes.split(/\s+/);
  const baseClass = classArray.find(
    (cls) => cls.startsWith(prefix) && !cls.includes(':'),
  );

  if (!baseClass) return 0;

  const spacingValue = baseClass.replace(prefix, '');
  return tailwindSpacingToPx[spacingValue] ?? 0;
}

// Helper function to parse CSS value to pixels
function parseCssValueToPx(cssValue: string | undefined): number {
  if (!cssValue) return 0;
  const numericMatch = cssValue.match(/^(\d+(?:\.\d+)?)/);
  if (!numericMatch) return 0;

  const value = parseFloat(numericMatch[1]);
  if (cssValue.includes('px')) {
    return value;
  } else if (cssValue.includes('rem')) {
    return value * 16; // 1rem = 16px
  }
  return value;
}

// Calculate total minimum width from all columns
const computedMinWidth = computed(() => {
  const fields = isTablet.value ? props.tableFieldsTablet : props.tableFields;
  if (!fields || fields.length === 0) {
    return '100%';
  }

  let totalMinWidth = 0;
  fields.forEach((field) => {
    const minWidthValue = isTablet.value
      ? field.minWidth.tablet
      : field.minWidth.desktop;

    if (minWidthValue) {
      totalMinWidth += parseCssValueToPx(minWidthValue);
    }
  });

  // Add custom padding values (left + right) from Tailwind classes
  const paddingLeft = parseTailwindPaddingToPx(props.customPaddingLeft, 'left');
  const paddingRight = parseTailwindPaddingToPx(
    props.customPaddingRight,
    'right',
  );
  const totalPadding = paddingLeft + paddingRight;

  return totalMinWidth > 0 ? `${totalMinWidth + totalPadding}px` : '100%';
});

// Computed items with guaranteed unique id for DynamicScroller
const computedItems = computed(() => {
  return props.items.map((item, index) => {
    // If item already has the keyField, return as-is
    if (item[props.keyField] !== undefined && item[props.keyField] !== null) {
      return item;
    }

    // Generate a unique id for combination mode items
    let generatedId: string;

    // TyreBikeModelTyreItem has item.tyres.front/rear
    if (item.tyres?.front?.articleNo && item.tyres?.rear?.articleNo) {
      generatedId = `${item.tyres.front.articleNo}-${item.tyres.rear.articleNo}`;
    }
    // TyrePairItem has item.front/rear
    else if (item.front?.articleNo && item.rear?.articleNo) {
      generatedId = `${item.front.articleNo}-${item.rear.articleNo}`;
    }
    // Fallback to index-based id
    else {
      generatedId = item.id?.toString() || item.articleNo?.toString() || item.ean?.toString() || `item-${index}`;
    }

    // Return item with generated id added
    return {
      ...item,
      [props.keyField]: generatedId,
      _originalItem: item,
    };
  });
});

// ==============================================
// Helper Functions
// ==============================================
function getItemId(item: any): string {
  // Use configured keyField first
  const keyValue = item[props.keyField];
  if (keyValue !== undefined && keyValue !== null) {
    return keyValue.toString();
  }

  // Handle combination mode items (TyrePairItem or TyreBikeModelTyreItem)
  // TyreBikeModelTyreItem has item.tyres.front/rear
  // TyrePairItem has item.front/rear
  if (item.tyres?.front?.articleNo && item.tyres?.rear?.articleNo) {
    return `${item.tyres.front.articleNo}-${item.tyres.rear.articleNo}`;
  }
  if (item.front?.articleNo && item.rear?.articleNo) {
    return `${item.front.articleNo}-${item.rear.articleNo}`;
  }

  // Fallback to common identifiers
  return item.id?.toString() || item.articleNo?.toString() || item.ean?.toString() || '';
}

function getRowClass(index: number): string {
  if (props.inverse) {
    return index % 2 === 1 ? 'bg-zbr-gray-300' : 'bg-white';
  }
  return index % 2 === 1 ? 'bg-white' : 'bg-zbr-gray-300';
}

function isNoLongerProducedProduct(
  item:
    | TyrePairItem
    | TyreBikeModelTyreItem
    | TyreItemResponse
    | TyreBargainItem
    | TyreDiscountItem
    | TyreAccessoryItem,
  axis?: 'front' | 'rear',
): boolean {
  if (item === undefined || props.showAllField) {
    return false;
  }
  let tyreItem;
  if (!axis) {
    tyreItem = item as
      | TyreItemResponse
      | TyreBargainItem
      | TyreDiscountItem
      | TyreAccessoryItem;
    return (
      tyreItem.articleNo === '8888888888' || tyreItem.articleNo === '9999999999'
    );
  }

  if (Object.keys(item).includes('tyres')) {
    tyreItem = item as TyreBikeModelTyreItem;
    return (
      tyreItem.tyres[axis].articleNo === '8888888888' ||
      tyreItem.tyres[axis].articleNo === '9999999999'
    );
  }

  tyreItem = item as TyrePairItem;
  return (
    tyreItem[axis].articleNo === '8888888888' ||
    tyreItem[axis].articleNo === '9999999999'
  );
}

function isPairTyreShowCart(item: TyrePairItem | TyreBikeModelTyreItem): boolean {
  if (item === undefined || props.showAllField) {
    return false;
  }

  let tyreItem;
  if (Object.keys(item).includes('tyres')) {
    tyreItem = item as TyreBikeModelTyreItem;
    return (
      (tyreItem.tyres.front.comments.includes('TOOLS-NO-PRODUCTION-2022') ||
        tyreItem.tyres.front.articleNo === '8888888888' ||
        tyreItem.tyres.front.articleNo === '9999999999') &&
      (tyreItem.tyres.rear.comments.includes('TOOLS-NO-PRODUCTION-2022') ||
        tyreItem.tyres.rear.articleNo === '8888888888' ||
        tyreItem.tyres.rear.articleNo === '9999999999')
    );
  }

  tyreItem = item as TyrePairItem;
  return (
    (tyreItem.front.comments.includes('TOOLS-NO-PRODUCTION-2022') ||
      tyreItem.front.articleNo === '8888888888' ||
      tyreItem.front.articleNo === '9999999999') &&
    (tyreItem.rear.comments.includes('TOOLS-NO-PRODUCTION-2022') ||
      tyreItem.rear.articleNo === '8888888888' ||
      tyreItem.rear.articleNo === '9999999999')
  );
}

function isCommentNoProduction(
  item:
    | TyrePairItem
    | TyreBikeModelTyreItem
    | TyreItemResponse
    | TyreBargainItem
    | TyreDiscountItem
    | TyreAccessoryItem,
  axis?: 'front' | 'rear',
): boolean {
  if (item === undefined || props.showAllField) {
    return false;
  }
  let tyreItem;
  if (!axis) {
    tyreItem = item as
      | TyreItemResponse
      | TyreBargainItem
      | TyreDiscountItem
      | TyreAccessoryItem;
    return Boolean(
      tyreItem.comments &&
      tyreItem.comments.length > 0 &&
      tyreItem.comments.includes('TOOLS-NO-PRODUCTION-2022'),
    );
  }

  if (Object.keys(item).includes('tyres')) {
    tyreItem = item as TyreBikeModelTyreItem;
    return Boolean(
      tyreItem.tyres[axis].comments &&
      tyreItem.tyres[axis].comments.length > 0 &&
      tyreItem.tyres[axis].comments.includes('TOOLS-NO-PRODUCTION-2022'),
    );
  }

  tyreItem = item as TyrePairItem;
  return Boolean(
    tyreItem[axis].comments &&
    tyreItem[axis].comments.length > 0 &&
    tyreItem[axis].comments.includes('TOOLS-NO-PRODUCTION-2022'),
  );
}

function isProduct99(
  item:
    | TyrePairItem
    | TyreBikeModelTyreItem
    | TyreItemResponse
    | TyreBargainItem
    | TyreDiscountItem
    | TyreAccessoryItem,
  axis?: 'front' | 'rear',
): boolean {
  if (item === undefined || props.showAllField) {
    return false;
  }
  let tyreItem;
  if (!axis) {
    tyreItem = item as
      | TyreItemResponse
      | TyreBargainItem
      | TyreDiscountItem
      | TyreAccessoryItem;
    return isArticle99(tyreItem);
  }

  if (Object.keys(item).includes('tyres')) {
    tyreItem = item as TyreBikeModelTyreItem;
    return isArticle99(tyreItem.tyres[axis]);
  }

  tyreItem = item as TyrePairItem;
  return isArticle99(tyreItem[axis]);
}

function isProduct88(
  item:
    | TyrePairItem
    | TyreBikeModelTyreItem
    | TyreItemResponse
    | TyreBargainItem
    | TyreDiscountItem
    | TyreAccessoryItem,
  axis?: 'front' | 'rear',
): boolean {
  if (item === undefined || props.showAllField) {
    return false;
  }
  let tyreItem;
  if (!axis) {
    tyreItem = item as
      | TyreItemResponse
      | TyreBargainItem
      | TyreDiscountItem
      | TyreAccessoryItem;
    return isArticle88(tyreItem);
  }

  if (Object.keys(item).includes('tyres')) {
    tyreItem = item as TyreBikeModelTyreItem;
    return isArticle88(tyreItem.tyres[axis]);
  }

  tyreItem = item as TyrePairItem;
  return isArticle88(tyreItem[axis]);
}

function isNoStock(
  item:
    | TyrePairItem
    | TyreBikeModelTyreItem
    | TyreItemResponse
    | TyreBargainItem
    | TyreDiscountItem
    | TyreAccessoryItem,
  axis?: 'front' | 'rear',
): boolean {
  if (item === undefined || props.showAllField) {
    return false;
  }
  let tyreItem;
  if (!axis) {
    tyreItem = item as
      | TyreItemResponse
      | TyreBargainItem
      | TyreDiscountItem
      | TyreAccessoryItem;
    return tyreItem.stock === 0;
  }

  if (Object.keys(item).includes('tyres')) {
    tyreItem = item as TyreBikeModelTyreItem;
    return tyreItem.tyres[axis].stock === 0;
  }

  tyreItem = item as TyrePairItem;
  return tyreItem[axis].stock === 0;
}

function isVehicleSearchDisableClickRow(
  item: any,
  axis: 'front' | 'rear',
): boolean {
  let tableItem;
  if (Object.keys(item).includes('tyres')) {
    tableItem = item.tyres[axis];
  } else {
    tableItem = item[axis];
  }

  return (
    !!props.isVehicleSearch &&
    (isNoInfoAndArticleAA(tableItem) || isNoInfoAndArticle88(tableItem))
  );
}

function isPremiumCombinationTyres(tableItem: any): boolean {
  if (!props.combinationType) {
    return false;
  }
  if (props.combinationType === 'vehicleSearch') {
    return tableItem.tyres.front.isPremium && tableItem.tyres.rear.isPremium;
  }
  if (props.combinationType === 'tyreSearch') {
    return tableItem.front.isPremium && tableItem.rear.isPremium;
  }
  return false;
}

// ==============================================
// Row Interaction Methods
// ==============================================
function onClickTableRow(
  tableItem: DynamicKeyValue,
  index: number,
  axis?: string,
): void {
  if (!props.clickable) {
    return;
  }

  if (tableItem.disableClickTableRow) {
    return;
  }

  emit('row-click', {
    tableItem,
    index,
  });
  toggleTableRowExpand(tableItem, index, axis);
}

function toggleRowMoreInfo(itemId: string, axis?: string): void {
  if (props.alwaysShowMoreInfo) {
    return;
  }
  if (!props.isCombination) {
    showRowMoreInfo.value = {
      ...showRowMoreInfo.value,
      [itemId]: !showRowMoreInfo.value[itemId],
    };
    emit('toggle-row-more-info');
    return;
  }

  if (axis === 'front') {
    showRowMoreInfoFront.value = {
      ...showRowMoreInfoFront.value,
      [itemId]: !showRowMoreInfoFront.value[itemId],
    };
    emit('toggle-row-more-info');
  } else {
    showRowMoreInfoRear.value = {
      ...showRowMoreInfoRear.value,
      [itemId]: !showRowMoreInfoRear.value[itemId],
    };
    emit('toggle-row-more-info');
  }
}

function toggleTableRowExpand(
  tableItem: DynamicKeyValue,
  index: number,
  axis?: string,
): void {
  const itemId = getItemId(tableItem);

  if (!props.isCombination) {
    expandRowStatus.value = {
      ...expandRowStatus.value,
      [itemId]: !expandRowStatus.value[itemId],
    };
    emit('toggle-table-row-expand', {
      tableItem,
      index,
      expandRowStatus: expandRowStatus.value[itemId],
    });
    return;
  }
  if (axis === 'front') {
    expandRowStatusFront.value = {
      ...expandRowStatusFront.value,
      [itemId]: !expandRowStatusFront.value[itemId],
    };
    emit('toggle-table-row-expand', {
      tableItem,
      index,
      expandRowStatus: expandRowStatusFront.value[itemId],
    });
  } else {
    expandRowStatusRear.value = {
      ...expandRowStatusRear.value,
      [itemId]: !expandRowStatusRear.value[itemId],
    };
    emit('toggle-table-row-expand', {
      tableItem,
      index,
      expandRowStatus: expandRowStatusRear.value[itemId],
    });
  }
}

function closeTableRowExpand(itemId: string, axis?: string): void {
  if (!props.isCombination) {
    expandRowStatus.value = {
      ...expandRowStatus.value,
      [itemId]: false,
    };
    emit('close-table-row-expand');
    return;
  }
  if (axis === 'front') {
    expandRowStatusFront.value = {
      ...expandRowStatusFront.value,
      [itemId]: false,
    };
    emit('close-table-row-expand');
  } else {
    expandRowStatusRear.value = {
      ...expandRowStatusRear.value,
      [itemId]: false,
    };
    emit('close-table-row-expand');
  }
}

// ==============================================
// Scroll Pagination
// ==============================================
const onScrollEnd = useDebounceFn(() => {
  if (props.isPageEnd || isDisabledNextPage.value || props.isLoading) {
    return;
  }
  pageNumber.value++;
  isDisabledNextPage.value = true;
  emit('next-page', pageNumber.value);
}, 100);

function resetPageNumber(): void {
  pageNumber.value = 1;
  if (scrollerRef.value) {
    scrollerRef.value.scrollToItem(0);
  }
}

// ==============================================
// State Initialization
// ==============================================
function initializeStateForItem(item: any): void {
  const itemId = getItemId(item);
  if (itemId && expandRowStatus.value[itemId] === undefined) {
    const defaultMoreInfo = props.alwaysShowMoreInfo || props.isInitOpenShowMoreInfo || false;

    expandRowStatus.value[itemId] = false;
    expandRowStatusFront.value[itemId] = false;
    expandRowStatusRear.value[itemId] = false;
    showRowMoreInfo.value[itemId] = defaultMoreInfo;
    showRowMoreInfoFront.value[itemId] = defaultMoreInfo;
    showRowMoreInfoRear.value[itemId] = defaultMoreInfo;
  }
}

function initializeAllStates(): void {
  props.items.forEach((item) => {
    initializeStateForItem(item);
  });
}

// ==============================================
// Watchers
// ==============================================
watch(() => props.items, () => {
  initializeAllStates();
}, { immediate: true, deep: true });

watch(() => props.isLoading, (val) => {
  if (!val) {
    isDisabledNextPage.value = false;
  }
});

watch(() => props.isPageEnd, (val) => {
  if (val) {
    isDisabledNextPage.value = true;
  }
});

// ==============================================
// Lifecycle
// ==============================================
onMounted(() => {
  initializeAllStates();
});

// ==============================================
// Close All Show More Info
// ==============================================
function closeAllShowMoreInfo(): void {
  const closedState: ShowRowMoreInfo = {};
  props.items.forEach((item) => {
    const itemId = getItemId(item);
    closedState[itemId] = false;
  });
  showRowMoreInfo.value = closedState;
  showRowMoreInfoFront.value = { ...closedState };
  showRowMoreInfoRear.value = { ...closedState };
}

// ==============================================
// Expose
// ==============================================
__expose({
  resetPageNumber,
  scrollerRef,
  closeAllShowMoreInfo,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "dynamic-scroller-table relative h-full overflow-x-auto overflow-y-hidden",
    style: _normalizeStyle({
      maxHeight: _ctx.maxHeight || '100%', minWidth: computedMinWidth.value })
  }, [
    _createVNode(_unref(DynamicScroller), {
      ref_key: "scrollerRef",
      ref: scrollerRef,
      items: computedItems.value,
      "min-item-size": 52,
      "key-field": _ctx.keyField,
      class: "h-full overflow-y-auto",
      onScrollEnd: _unref(onScrollEnd)
    }, {
      before: _withCtx(() => [
        _renderSlot(_ctx.$slots, "bodyPrepend")
      ]),
      default: _withCtx(({ item, index, active }) => [
        _createVNode(_unref(DynamicScrollerItem), {
          item: item,
          active: active,
          "data-index": index,
          "size-dependencies": [
            showRowMoreInfo.value[getItemId(item)],
            showRowMoreInfoFront.value[getItemId(item)],
            showRowMoreInfoRear.value[getItemId(item)],
            expandRowStatus.value[getItemId(item)],
            expandRowStatusFront.value[getItemId(item)],
            expandRowStatusRear.value[getItemId(item)],
          ]
        }, {
          default: _withCtx(() => [
            (!_ctx.isCombination)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass([
                'relative tbody-tr grid grid-cols-base-table-tablet items-center w-full min-h-[52px] group hover:bg-zbr-primary-50',
                'xl:grid-cols-base-table-xl',
                '2xl:grid-cols-base-table-2xl',
                getRowClass(index),
                _ctx.hasPaddingLeft
                  ? _ctx.customPaddingLeft
                    ? _ctx.customPaddingLeft
                    : 'pl-6'
                  : null,
                _ctx.customPaddingRight ? _ctx.customPaddingRight : null,
                _ctx.customGap ? _ctx.customGap : null,
                _ctx.clickable && _ctx.hasInfoFieldDesktop ? 'group' : null,
                _ctx.rowsTableError && _ctx.rowsTableError[index] ? '!bg-red-100' : null,
                item.isPremium ? 'is-premium-tyre' : '',
              ])
                }, [
                  (_ctx.clickable && _ctx.hasInfoFieldDesktop)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: "hidden xl:block absolute w-full h-full click-area z-[1]",
                        onClick: ($event: any) => (onClickTableRow(item, index))
                      }, null, 8, _hoisted_1))
                    : _createCommentVNode("", true),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(computedTableFields.value, (field, fieldIndex) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: `virtualTableBodyTdDesktop${index}${fieldIndex}-${field.value}`,
                      class: _normalizeClass([
                  'table-body-data flex items-center min-h-[52px] group-hover:bg-zbr-primary-50',
                  _ctx.tableBodyClass ? _ctx.tableBodyClass : 'text-sm',
                  field.value === 'cart' ? 'h-full justify-center' : null,
                  field.isAction ? 'relative z-10' : null,
                  getRowClass(index),
                  field.align,
                  field.flexJustify,
                  field.value,
                  _ctx.rowsTableError && _ctx.rowsTableError[index] ? '!bg-red-100' : null,
                ])
                    }, [
                      (
                    [
                      'quantity',
                      'cart',
                      'price',
                      'info',
                      'calculationBasisPrice',
                      'salePrice',
                      'purchasingPrice',
                      'reductionPrice',
                    ].includes(field.value) &&
                      isNoLongerProducedProduct(item)
                  )
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [], 64))
                        : (
                    ['quantity', 'cart'].includes(field.value.toLowerCase()) &&
                      isCommentNoProduction(item) &&
                      item.stock === 0
                  )
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [], 64))
                          : (
                    (isProduct88(item) || isProduct99(item)) &&
                      field.value.toLowerCase() === 'availability'
                  )
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [], 64))
                            : (
                    (isProduct88(item) || isProduct99(item)) &&
                      field.value.toLowerCase() === 'image'
                  )
                              ? (_openBlock(), _createBlock(IconInformationTransparent, {
                                  key: 3,
                                  style: {"margin-left":"10px"}
                                }))
                              : (_unref(slots)[`td(${field.value})`])
                                ? _renderSlot(_ctx.$slots, `td(${field.value})`, {
                                    key: 4,
                                    item: item,
                                    index: index,
                                    value: item[field.value],
                                    toggleExpandRow: 
                    () => toggleTableRowExpand(item, index)
                  ,
                                    toggleRowMoreInfo: () => toggleRowMoreInfo(getItemId(item)),
                                    expandRowStatus: expandRowStatus.value[getItemId(item)],
                                    showRowMoreInfo: showRowMoreInfo.value[getItemId(item)]
                                  })
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                                    _createTextVNode(_toDisplayString(item[field.value]), 1)
                                  ], 64))
                    ], 2))
                  }), 128)),
                  (_unref(isTablet) && showRowMoreInfo.value[getItemId(item)])
                    ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(_ctx.tableFieldsTabletMoreInfo, (field, fieldIndex) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: `virtualTableBodyTdTabletMoreInfo${index}${fieldIndex}`,
                          class: _normalizeClass([
                    'table-body-data flex items-center h-[68px] border-zbr-gray-300 border-t',
                    _ctx.tableBodyClass ? _ctx.tableBodyClass : 'text-sm',
                    field.value === 'cart' ? 'h-full justify-center' : null,
                    field.isAction ? 'relative z-10' : null,
                    field.colSpan ? field.colSpan : '',
                    field.align,
                    field.flexJustify,
                    field.value,
                    _ctx.rowsTableError && _ctx.rowsTableError[index]
                      ? '!bg-red-100'
                      : null,
                  ])
                        }, [
                          _renderSlot(_ctx.$slots, `td(${field.value}TabletMore)`, {
                            item: item,
                            index: index,
                            value: item[field.value]
                          })
                        ], 2))
                      }), 128))
                    : _createCommentVNode("", true),
                  (expandRowStatus.value[getItemId(item)] || item.isExpand)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 2,
                        class: _normalizeClass([
                  'table-expand relative row-end-auto min-h-[52px] z-[21]',
                  _ctx.expandColSpan,
                ])
                      }, [
                        _renderSlot(_ctx.$slots, "tdExpand", {
                          item: item,
                          index: index,
                          closeExpandRow: () => closeTableRowExpand(getItemId(item))
                        })
                      ], 2))
                    : _createCommentVNode("", true)
                ], 2))
              : (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  id: `group-tr-${index}`,
                  class: _normalizeClass([
                'relative hover:bg-zbr-primary-50',
                getRowClass(index),
                isPremiumCombinationTyres(item) ? 'is-premium-tyre' : '',
              ])
                }, [
                  _createElementVNode("div", {
                    class: _normalizeClass([
                  'relative tbody-tr grid grid-cols-base-table-tablet items-center w-full min-h-[52px]',
                  'xl:grid-cols-base-table-xl',
                  '2xl:grid-cols-base-table-2xl',
                  _ctx.hasPaddingLeft
                    ? _ctx.customPaddingLeft
                      ? _ctx.customPaddingLeft
                      : 'pl-6'
                    : null,
                  _ctx.customPaddingRight ? _ctx.customPaddingRight : null,
                  _ctx.customGap ? _ctx.customGap : null,
                  _ctx.clickable &&
                    _ctx.hasInfoFieldDesktop &&
                    !isVehicleSearchDisableClickRow(item, 'front')
                    ? 'group'
                    : null,
                  _ctx.rowsTableError && _ctx.rowsTableError[index]
                    ? '!bg-red-100'
                    : null,
                ])
                  }, [
                    (
                    _ctx.clickable &&
                      _ctx.hasInfoFieldDesktop &&
                      !isVehicleSearchDisableClickRow(item, 'front')
                  )
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          class: "hidden xl:block absolute w-full h-full click-area z-[1]",
                          onClick: ($event: any) => (onClickTableRow(item, index, 'front'))
                        }, null, 8, _hoisted_3))
                      : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(computedTableFields.value, (field, fieldIndex) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: `virtualTableBodyTd${index}${fieldIndex}${computedTableFields.value.length}`,
                        class: _normalizeClass([
                    'table-body-data flex items-center min-h-[52px]',
                    _ctx.tableBodyClass ? _ctx.tableBodyClass : 'text-sm',
                    field.value === 'favourite' || field.value === 'cart'
                      ? 'h-full justify-center'
                      : null,
                    field.colSpan ? field.colSpan : '',
                    field.value === 'cart' && showRowMoreInfoFront.value[getItemId(item)]
                      ? '-col-start-1 row-span-4'
                      : field.value === 'cart'
                        ? '-col-start-1 row-span-2 z-20'
                        : '',
                    field.value === 'favourite' && showRowMoreInfoFront.value[getItemId(item)]
                      ? 'col-start-1 row-span-4'
                      : field.value === 'favourite'
                        ? 'col-start-1 row-span-2'
                        : '',
                    field.isAction ? 'relative z-20' : null,
                    field.align,
                    field.flexJustify,
                    field.value,
                    _ctx.rowsTableError && _ctx.rowsTableError[index]
                      ? '!bg-red-100'
                      : null,
                  ])
                      }, [
                        (
                      [
                        'quantity',
                        'price',
                        'info',
                        'quantityFront',
                        'priceFront',
                        'infoFront',
                        'calculationBasisPrice',
                        'salePrice',
                        'purchasingPrice',
                        'reductionPrice',
                      ].includes(field.value) &&
                        isNoLongerProducedProduct(item, 'front')
                    )
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [], 64))
                          : (
                      ['quantity', 'quantityFront'].includes(
                        field.value.toLowerCase()
                      ) &&
                        isCommentNoProduction(item, 'front') &&
                        isNoStock(item, 'front')
                    )
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [], 64))
                            : (
                      ['cart', 'cartFront'].includes(
                        field.value.toLowerCase()
                      ) &&
                        isPairTyreShowCart(item) &&
                        isNoLongerProducedProduct(item, 'front') &&
                        isNoLongerProducedProduct(item, 'rear')
                    )
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [], 64))
                              : (
                      ['cart', 'cartFront'].includes(
                        field.value.toLowerCase()
                      ) &&
                        isPairTyreShowCart(item) &&
                        isCommentNoProduction(item, 'front') &&
                        isNoStock(item, 'front') &&
                        isCommentNoProduction(item, 'rear') &&
                        isNoStock(item, 'rear')
                    )
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [], 64))
                                : (
                      (isProduct88(item, 'front') ||
                        isProduct99(item, 'front')) &&
                        ['availability', 'availabilityFront'].includes(
                          field.value.toLowerCase()
                        )
                    )
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [], 64))
                                  : (
                      (isProduct88(item, 'front') ||
                        isProduct99(item, 'front')) &&
                        ['image', 'imageFront'].includes(
                          field.value.toLowerCase()
                        )
                    )
                                    ? (_openBlock(), _createBlock(IconInformationTransparent, {
                                        key: 5,
                                        style: {"margin-left":"10px"}
                                      }))
                                    : (field.value === 'cart')
                                      ? _renderSlot(_ctx.$slots, "td(cart)", {
                                          key: 6,
                                          item: item,
                                          index: index,
                                          value: item['cart']
                                        })
                                      : (_unref(slots)[`td(${field.value}Front)`])
                                        ? _renderSlot(_ctx.$slots, `td(${field.value}Front)`, {
                                            key: 7,
                                            item: item,
                                            index: index,
                                            value: item[field.value],
                                            toggleExpandRow: 
                      () => toggleTableRowExpand(item, index, 'front')
                    ,
                                            toggleRowMoreInfo: 
                      () => toggleRowMoreInfo(getItemId(item), 'front')
                    ,
                                            expandRowStatus: expandRowStatusFront.value[getItemId(item)],
                                            showRowMoreInfo: showRowMoreInfoFront.value[getItemId(item)]
                                          })
                                        : (_openBlock(), _createElementBlock(_Fragment, { key: 8 }, [
                                            _createTextVNode(_toDisplayString(item[field.value]), 1)
                                          ], 64))
                      ], 2))
                    }), 128)),
                    (_unref(isTablet) && showRowMoreInfoFront.value[getItemId(item)])
                      ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(_ctx.tableFieldsTabletMoreInfo, (field, fieldIndex) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: `virtualTableBodyTdTabletMoreFront${index}${fieldIndex}`,
                            class: _normalizeClass([
                      'table-body-data flex items-center h-[68px] border-zbr-gray-300 border-b',
                      _ctx.tableBodyClass ? _ctx.tableBodyClass : 'text-sm',
                      field.value === 'cart' ? 'h-full justify-center' : null,
                      field.colSpan ? field.colSpan : '',
                      field.isAction ? 'relative z-10' : null,
                      field.align,
                      field.flexJustify,
                      field.value,
                      _ctx.rowsTableError && _ctx.rowsTableError[index]
                        ? '!bg-red-100'
                        : null,
                    ])
                          }, [
                            _renderSlot(_ctx.$slots, `td(${field.value}TabletMoreFront)`, {
                              item: item,
                              index: index,
                              value: item[field.value]
                            })
                          ], 2))
                        }), 128))
                      : _createCommentVNode("", true)
                  ], 2),
                  (expandRowStatusFront.value[getItemId(item)] || item.isExpand)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: _normalizeClass([
                  'table-expand relative min-h-[52px] z-[21]',
                  _ctx.expandColSpan,
                ])
                      }, [
                        _renderSlot(_ctx.$slots, "tdExpandFront", {
                          item: item,
                          index: index,
                          closeExpandRow: () => closeTableRowExpand(getItemId(item), 'front')
                        })
                      ], 2))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", {
                    class: _normalizeClass([
                  'relative tbody-tr grid grid-cols-base-table-tablet items-center w-full min-h-[52px]',
                  'xl:grid-cols-base-table-xl',
                  '2xl:grid-cols-base-table-2xl',
                  _ctx.hasPaddingLeft
                    ? _ctx.customPaddingLeft
                      ? _ctx.customPaddingLeft
                      : 'pl-6'
                    : null,
                  _ctx.customPaddingRight ? _ctx.customPaddingRight : null,
                  _ctx.customGap ? _ctx.customGap : null,
                  _ctx.rowsTableError && _ctx.rowsTableError[index]
                    ? '!bg-red-100'
                    : null,
                  _ctx.clickable &&
                    _ctx.hasInfoFieldDesktop &&
                    !isVehicleSearchDisableClickRow(item, 'rear')
                    ? 'group'
                    : null,
                  isVehicleSearchDisableClickRow(item, 'rear')
                    ? 'pb-[44px]'
                    : null,
                ])
                  }, [
                    (isVehicleSearchDisableClickRow(item, 'rear'))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_ctx.$t("BIKES-SUGGESTION")), 1))
                      : _createCommentVNode("", true),
                    (
                    _ctx.clickable &&
                      _ctx.hasInfoFieldDesktop &&
                      !isVehicleSearchDisableClickRow(item, 'rear')
                  )
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 1,
                          class: "hidden xl:block absolute w-full h-full click-area z-[1]",
                          onClick: ($event: any) => (onClickTableRow(item, index, 'rear'))
                        }, null, 8, _hoisted_5))
                      : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(computedTableFields.value, (field, fieldIndex) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: `virtualTableBodyTdRear${index}${fieldIndex}${computedTableFields.value.length}`,
                        class: _normalizeClass([
                    'table-body-data flex items-center min-h-[52px]',
                    _ctx.tableBodyClass ? _ctx.tableBodyClass : 'text-sm',
                    field.value === 'favourite' || field.value === 'cart'
                      ? 'h-full justify-center'
                      : null,
                    field.colSpan ? field.colSpan : '',
                    field.value === 'cart' && showRowMoreInfoRear.value[getItemId(item)]
                      ? '-col-start-1 row-span-4'
                      : field.value === 'cart'
                        ? '-col-start-1 row-span-2'
                        : '',
                    field.value === 'favourite' && showRowMoreInfoRear.value[getItemId(item)]
                      ? 'col-start-1 row-span-4'
                      : field.value === 'favourite'
                        ? 'col-start-1 row-span-2'
                        : '',
                    field.isAction ? 'relative z-10' : null,
                    field.align,
                    field.flexJustify,
                    field.value,
                    _ctx.rowsTableError && _ctx.rowsTableError[index]
                      ? '!bg-red-100'
                      : null,
                  ])
                      }, [
                        (
                      [
                        'quantity',
                        'quantityRear',
                        'cart',
                        'cartRear',
                        'price',
                        'priceRear',
                        'info',
                        'infoRear',
                        'calculationBasisPrice',
                        'salePrice',
                        'purchasingPrice',
                        'reductionPrice',
                      ].includes(field.value) &&
                        isNoLongerProducedProduct(item, 'rear')
                    )
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [], 64))
                          : (
                      ['quantity', 'quantityRear'].includes(
                        field.value.toLowerCase()
                      ) &&
                        isCommentNoProduction(item, 'rear') &&
                        isNoStock(item, 'rear')
                    )
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [], 64))
                            : (
                      (isProduct88(item, 'rear') ||
                        isProduct99(item, 'rear')) &&
                        ['availability', 'availabilityRear'].includes(
                          field.value.toLowerCase()
                        )
                    )
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [], 64))
                              : (
                      (isProduct88(item, 'rear') ||
                        isProduct99(item, 'rear')) &&
                        ['image', 'imageRear'].includes(field.value.toLowerCase())
                    )
                                ? (_openBlock(), _createBlock(IconInformationTransparent, {
                                    key: 3,
                                    style: {"margin-left":"10px"}
                                  }))
                                : (_unref(slots)[`td(${field.value}Rear)`])
                                  ? _renderSlot(_ctx.$slots, `td(${field.value}Rear)`, {
                                      key: 4,
                                      item: item,
                                      index: index,
                                      value: item[field.value],
                                      toggleExpandRow: 
                      () => toggleTableRowExpand(item, index, 'rear')
                    ,
                                      toggleRowMoreInfo: 
                      () => toggleRowMoreInfo(getItemId(item), 'rear')
                    ,
                                      expandRowStatus: expandRowStatusRear.value[getItemId(item)],
                                      showRowMoreInfo: showRowMoreInfoRear.value[getItemId(item)]
                                    })
                                  : (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                                      _createTextVNode(_toDisplayString(item[field.value]), 1)
                                    ], 64))
                      ], 2))
                    }), 128)),
                    (_unref(isTablet) && showRowMoreInfoRear.value[getItemId(item)])
                      ? (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(_ctx.tableFieldsTabletMoreInfo, (field, fieldIndex) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: `virtualTableBodyTdTabletMoreRear${index}${fieldIndex}`,
                            class: _normalizeClass([
                      'table-body-data flex items-center h-[68px]',
                      _ctx.tableBodyClass ? _ctx.tableBodyClass : 'text-sm',
                      field.value === 'cart' ? 'h-full justify-center' : null,
                      field.colSpan ? field.colSpan : '',
                      field.isAction ? 'relative z-10' : null,
                      field.align,
                      field.flexJustify,
                      field.value,
                      _ctx.rowsTableError && _ctx.rowsTableError[index]
                        ? '!bg-red-100'
                        : null,
                    ])
                          }, [
                            _renderSlot(_ctx.$slots, `td(${field.value}TabletMoreRear)`, {
                              item: item,
                              index: index,
                              value: item[field.value]
                            })
                          ], 2))
                        }), 128))
                      : _createCommentVNode("", true)
                  ], 2),
                  (expandRowStatusRear.value[getItemId(item)] || item.isExpand)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 1,
                        class: _normalizeClass([
                  'table-expand relative min-h-[52px] z-[21]',
                  _ctx.expandColSpan,
                ])
                      }, [
                        _renderSlot(_ctx.$slots, "tdExpandRear", {
                          item: item,
                          index: index,
                          closeExpandRow: () => closeTableRowExpand(getItemId(item), 'rear')
                        })
                      ], 2))
                    : _createCommentVNode("", true)
                ], 10, _hoisted_2))
          ]),
          _: 2
        }, 1032, ["item", "active", "data-index", "size-dependencies"])
      ]),
      after: _withCtx(() => [
        (!_ctx.isLoading && _ctx.items.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("p", null, _toDisplayString(_ctx.$t("NO-RESULT")), 1)
            ]))
          : _createCommentVNode("", true),
        _renderSlot(_ctx.$slots, "bodyAppend")
      ]),
      _: 3
    }, 8, ["items", "key-field", "onScrollEnd"])
  ], 4))
}
}

})